"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { useGetListPrices } from "@/hooks/requests/list-price/use-list-price";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { IListPrice } from "@/interfaces/list-price.interface";
import { ListPriceMessages } from "@/messages/listPrice/list-price.messages";

import ListPriceFiltersForm from "./components/ListPriceFiltersForm";
import { ListPriceTable } from "./components/ListPriceTable";
import { initialFilterValues } from "./constants";

type Props = {
  messages: ListPriceMessages;
};

function ListPricePage({ messages }: Props) {
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const filter = useFilters<IListPrice>({ initialValue: initialFilterValues });
  const filters = filter.filters;

  const { data, isLoading, error } = useGetListPrices({ filters: filters, pageNumber, pageSize });

  const shouldLoadListPriceTable = !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = !data && !isLoading && !!error;
  const shouldLoadNoFound =
    (!!data && !isLoading && !error && data.items.length === 0) || typeof data === "string";

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Formik initialValues={filters} validateOnChange={false} onSubmit={filter.changeFilters}>
          <ListPriceFiltersForm
            messages={messages}
            filters={filter.temporaryFilters}
            deleteFilter={filter.deleteTemporaryFilter}
          />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">{messages.listPriceTableTitle}</MDTypography>
              </MDBox>

              {!!shouldLoadListPriceTable && (
                <ListPriceTable pagination={pagination} data={data} messages={messages} />
              )}

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.listPriceErrorToLoad}
                  description={error.message}
                  alt={messages.listPriceNotFoundError}
                />
              )}

              {!!shouldLoadNoFound && (
                <AlertBlock
                  title={messages.listPriceNotFoundError}
                  description={messages.listPriceNotFoundDescription}
                  alt={messages.listPriceNotFoundError}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default ListPricePage;
